/**
 * Los avisos al telefono en ESTE aparato: encender, apagar, probar y mantener
 * al dia la suscripcion.
 *
 * El permiso lo pide el navegador y solo tras un toque: `enablePush` se llama
 * desde el boton de Configuracion, nunca solo. Lo que si se hace solo es
 * mantener al dia una suscripcion que ya existe (`resyncPush`), y siempre
 * respetando que la persona la haya apagado.
 *
 * Quien recibe que NO se decide aqui ni en ningun lado del navegador: lo decide
 * la base con la misma regla de la campanita (migracion 20261018000000). Aqui
 * solo se le dice al servidor "este aparato existe y es de quien tiene la
 * sesion".
 */

import {
  pushDevicesSchema,
  pushPublicKeySchema,
  pushTestResultSchema,
  type PushDevice,
  type PushTestResult,
} from '@abarrotes/shared';
import { api, ApiError } from './api.js';
import { currentEnv, deviceName } from './device.js';
import { currentPushSubscription, pushRegistration, pushSupport, type PushSupport } from './push-device.js';

export type PushState =
  | { kind: 'unsupported'; support: Exclude<PushSupport, 'yes'> }
  /** La app no tiene su service worker: desarrollo, o recien abierta. */
  | { kind: 'no-worker' }
  /** El navegador los tiene bloqueados: solo se desbloquean desde su ajuste. */
  | { kind: 'blocked' }
  | { kind: 'off' }
  | { kind: 'on'; endpoint: string };

export type PushFailure =
  /** `dismissed`: cerro el cuadro sin contestar; se le puede volver a pedir. */
  | { ok: false; reason: 'denied' | 'dismissed' }
  | { ok: false; reason: 'error'; message: string };

export type PushOutcome = { ok: true } | PushFailure;
export type PushValue<T> = { ok: true; value: T } | PushFailure;

/** Lo que la persona eligio en este aparato, para no volver a prenderlo solo. */
const OFF_KEY = 'abarrotes.push.off';

const readOff = (): boolean => {
  try {
    return localStorage.getItem(OFF_KEY) === '1';
  } catch {
    return false;
  }
};

/** La persona los apago a proposito en este aparato: nada los vuelve a ofrecer solo. */
export const pushTurnedOff = readOff;

const saveOff = (off: boolean): void => {
  try {
    if (off) localStorage.setItem(OFF_KEY, '1');
    else localStorage.removeItem(OFF_KEY);
  } catch {
    // Sin almacenamiento solo se pierde el recuerdo de la eleccion.
  }
};

const failure = (err: unknown, fallback: string): PushFailure => ({
  ok: false,
  reason: 'error',
  message: err instanceof ApiError ? err.message : err instanceof Error && err.message ? err.message : fallback,
});

export async function readPushState(): Promise<PushState> {
  const support = pushSupport();
  if (support !== 'yes') return { kind: 'unsupported', support };
  const registration = await pushRegistration();
  if (!registration) return { kind: 'no-worker' };
  if (Notification.permission === 'denied') return { kind: 'blocked' };
  const subscription = await registration.pushManager.getSubscription();
  return subscription && Notification.permission === 'granted'
    ? { kind: 'on', endpoint: subscription.endpoint }
    : { kind: 'off' };
}

// ---------------------------------------------------------------------------
// Con el servidor
// ---------------------------------------------------------------------------

function fromB64u(s: string): Uint8Array<ArrayBuffer> {
  const binary = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

const sameKey = (a: ArrayBuffer | null, b: Uint8Array): boolean => {
  if (!a || a.byteLength !== b.byteLength) return false;
  const x = new Uint8Array(a);
  return x.every((v, i) => v === b[i]);
};

/** La llave publica del servidor: con ella se suscribe el aparato. */
async function serverKey(): Promise<Uint8Array<ArrayBuffer>> {
  const { key } = pushPublicKeySchema.parse(await api('/api/push/key'));
  return fromB64u(key);
}

/** Suscribe con la llave vigente; si la del aparato es de otra, la cambia. */
async function subscribe(
  registration: ServiceWorkerRegistration,
  key: Uint8Array<ArrayBuffer>,
): Promise<PushSubscription> {
  const existing = await registration.pushManager.getSubscription();
  if (existing && sameKey(existing.options.applicationServerKey, key)) return existing;
  await existing?.unsubscribe();
  return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
}

async function save(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  await api('/api/push/subscription', {
    method: 'POST',
    body: {
      endpoint: subscription.endpoint,
      p256dh: json.keys?.['p256dh'] ?? '',
      auth: json.keys?.['auth'] ?? '',
      deviceLabel: deviceName(currentEnv()),
    },
  });
}

// ---------------------------------------------------------------------------
// Lo que hace Configuracion
// ---------------------------------------------------------------------------

/**
 * Pide el permiso, suscribe este aparato y lo deja a nombre de la sesion.
 *
 * EL PERMISO SE PIDE ANTES DE CUALQUIER ESPERA, en el mismo toque. Safari en
 * iPhone solo enseña el cuadro de permiso si la peticion sale directo del toque
 * de la persona; con una espera de por medio —leer el registro, pedir la llave—
 * el cuadro no aparece nunca, y sin decir nada. Es la leccion de Xokoyotl, y
 * por eso el registro y la llave van en paralelo DESPUES de lanzar la pregunta.
 */
export async function enablePush(): Promise<PushOutcome> {
  const asking = Notification.requestPermission();
  const [permission, registration, key] = await Promise.all([
    asking,
    pushRegistration(),
    serverKey().then(
      (value) => ({ ok: true as const, value }),
      (err: unknown) => ({ ok: false as const, err }),
    ),
  ]);

  if (permission === 'denied') return { ok: false, reason: 'denied' };
  if (permission !== 'granted') return { ok: false, reason: 'dismissed' };
  if (!registration) {
    return {
      ok: false,
      reason: 'error',
      message: 'La app todavia no termina de instalarse en este aparato. Recarga e intenta de nuevo.',
    };
  }
  if (!key.ok) return failure(key.err, 'No se pudo leer la llave de los avisos.');

  try {
    await save(await subscribe(registration, key.value));
    saveOff(false);
    return { ok: true };
  } catch (err) {
    return failure(err, 'El navegador no dejo encender los avisos.');
  }
}

/** Este aparato deja de recibir avisos, y se recuerda que se apagaron. */
export async function disablePush(): Promise<PushOutcome> {
  saveOff(true);
  const subscription = await currentPushSubscription();
  if (!subscription) return { ok: true };
  try {
    await api('/api/push/subscription', { method: 'DELETE', body: { endpoint: subscription.endpoint } });
    await subscription.unsubscribe();
    return { ok: true };
  } catch (err) {
    // Se suelta en el aparato aunque el servidor no conteste: lo que la persona
    // pidio es dejar de recibirlos AQUI, y el servidor borra la suscripcion
    // muerta solo la primera vez que el servicio de push le diga que no existe.
    await subscription.unsubscribe().catch(() => false);
    return failure(err, 'No se pudo avisar al servidor; en este aparato ya quedaron apagados.');
  }
}

/** Una de prueba a todos los aparatos de quien la pide. */
export async function sendTestPush(): Promise<PushValue<PushTestResult>> {
  try {
    return { ok: true, value: pushTestResultSchema.parse(await api('/api/push/test', { method: 'POST' })) };
  } catch (err) {
    return failure(err, 'No se pudo mandar la prueba.');
  }
}

/** Los aparatos de quien pregunta donde los avisos estan encendidos. */
export async function listPushDevices(currentEndpoint: string | null): Promise<PushDevice[]> {
  const { devices } = pushDevicesSchema.parse(
    await api('/api/push/devices', { query: currentEndpoint ? { endpoint: currentEndpoint } : {} }),
  );
  return devices;
}

/**
 * Al abrir la app con sesion: si este aparato ya tenia los avisos, se le
 * recuerda al servidor de quien son ahora y con que llave. Cubre tres casos que
 * ningun boton ve: el telefono prestado que cambio de sesion, el navegador que
 * renovo su suscripcion por su cuenta (no se escucha `pushsubscriptionchange`
 * en el service worker: ahi no hay sesion con que guardarla), y la llave del
 * servidor que cambio. Nunca pide permiso ni prende lo que la persona apago.
 */
export async function resyncPush(): Promise<void> {
  if (pushSupport() !== 'yes' || readOff()) return;
  if (Notification.permission !== 'granted') return;
  const registration = await pushRegistration();
  if (!registration || !(await registration.pushManager.getSubscription())) return;
  try {
    await save(await subscribe(registration, await serverKey()));
  } catch {
    // Se intenta otra vez la proxima vez que se abra.
  }
}
